import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import {
  Camera,
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  X,
  Check,
  Palette,
  Package,
  Image,
  Tag,
  AlertTriangle,
  Truck,
  CreditCard,
  Banknote,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCart, CartProvider } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientGalleryContent = () => {
  const { accessCode } = useParams();
  const navigate = useNavigate();
  const { items, addItem, removeItem, updateQuantity, getTotal, itemCount, clearCart } = useCart();
  
  const [gallery, setGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [backgrounds, setBackgrounds] = useState([]);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [orderDialog, setOrderDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [customizations, setCustomizations] = useState({});
  const [selectedVariations, setSelectedVariations] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  
  // Package item configuration
  const [packageItemSelections, setPackageItemSelections] = useState({});
  const [activePackageItemIndex, setActivePackageItemIndex] = useState(0);

  const [customerForm, setCustomerForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });

  const [delivery, setDelivery] = useState({
    option: '',
    address: '',
    city: '',
    postal_code: '',
    address_type: '',
    notes: ''
  });
  
  const deliveryOptions = [
    { id: 'local', name: 'Personal Local Delivery', description: 'In Spruce Grove/Stony Plain', fee: 5 },
    { id: 'rural', name: 'Personal Delivery', description: 'Within 20km of Stony Plain/Rural', fee: 8 },
    { id: 'mail', name: 'Direct from Lab Delivery', description: 'Letter Rate (Max 8x12/500g)', fee: 10 }
  ];

  const [paymentMethod, setPaymentMethod] = useState('cash'); // 'cash' or 'card'

  const galleryTaxEnabled = gallery?.tax_enabled || false;
  const galleryTaxRate = gallery?.tax_rate || 5.0;

  useEffect(() => {
    fetchGalleryData();
  }, [accessCode]);

  const fetchGalleryData = async () => {
    try {
      const galleryRes = await axios.get(`${API}/client/gallery/${accessCode}`);
      setGallery(galleryRes.data.gallery);
      setPhotos(galleryRes.data.photos);
      setProducts(galleryRes.data.products || []);
      setPackages(galleryRes.data.packages || []);
      setBackgrounds(galleryRes.data.backgrounds || []);
      setIsExpired(galleryRes.data.gallery.is_expired || false);
    } catch (err) {
      setError('Gallery not found or not available');
    } finally {
      setLoading(false);
    }
  };

  const getWatermarkedUrl = (photo) => `${API}/photos/${photo.id}/watermarked`;

  const applyDiscountCode = async () => {
    if (!discountCode.trim()) {
      toast.error('Please enter a discount code');
      return;
    }
    try {
      const response = await axios.post(`${API}/discount-codes/validate?code=${discountCode}&subtotal=${getTotal()}&gallery_id=${gallery?.id || ''}`);
      setAppliedDiscount(response.data);
      toast.success(`Discount applied: ${response.data.discount_type === 'percent' ? `${response.data.discount_value}%` : `$${response.data.discount_value}`} off`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid discount code');
      setAppliedDiscount(null);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
  };

  const getDiscountedTotal = () => {
    const subtotal = getTotal();
    const discount = appliedDiscount?.discount_amount || 0;
    return subtotal - discount;
  };

  const getTaxAmount = () => {
    if (!galleryTaxEnabled) return 0;
    return getDiscountedTotal() * (galleryTaxRate / 100);
  };

  // Check if cart contains only digital products (no physical delivery needed)
  // Check if order is digital-only or all items are free ($0)
  const isDigitalOnlyOrFreeOrder = () => {
    if (items.length === 0) return false;
    
    // Check if all items are free ($0)
    const allFree = items.every(item => {
      if (item.product_id && item.product) {
        return (item.product.price || 0) === 0;
      } else if (item.package_id && item.package) {
        return (item.package.price || 0) === 0;
      }
      return false;
    });
    
    if (allFree) return true;
    
    // Check if all items are digital
    return items.every(item => {
      if (item.product_id && item.product) {
        return item.product.category === 'digital';
      } else if (item.package_id && item.package) {
        const pkgItems = item.package.items || [];
        return pkgItems.every(pkgItem => {
          const product = products.find(p => p.id === pkgItem.product_id);
          return product?.category === 'digital';
        });
      }
      return false;
    });
  };
  
  // Keep old name for backwards compatibility
  const isDigitalOnlyOrder = isDigitalOnlyOrFreeOrder;

  const getDeliveryFee = () => {
    // No delivery fee for digital-only orders
    if (isDigitalOnlyOrder()) return 0;
    const option = deliveryOptions.find(o => o.id === delivery.option);
    return option ? option.fee : 0;
  };

  const getCashDiscount = () => {
    return 0; // Cash discount disabled (June 2026)
  };

  const getGrandTotal = () => getDiscountedTotal() + getTaxAmount() + getDeliveryFee() - getCashDiscount();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  const openPhotoOrder = (photo) => {
    setSelectedPhoto(photo);
    setSelectedProduct(null);
    setSelectedPackage(null);
    setSelectedBackground(null);
    setCustomizations({});
    setSelectedVariations({});
    setPackageItemSelections({});
    setActivePackageItemIndex(0);
    setOrderDialog(true);
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setSelectedPackage(null);
    setSelectedVariations({});
    setPackageItemSelections({});
    setSelectedBackground(null);
    setCustomizations({});
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setSelectedProduct(null);
    setSelectedVariations({});
    setSelectedBackground(null);
    setCustomizations({});
    
    // Initialize selections for each package item
    const initialSelections = {};
    pkg.items?.forEach((item, index) => {
      const product = products.find(p => p.id === item.product_id);
      initialSelections[index] = {
        product_id: item.product_id,
        product: product,
        quantity: item.quantity,
        photo_id: selectedPhoto?.id,
        photo: selectedPhoto,
        customizations: {},
        background_id: null,
        background: null
      };
    });
    setPackageItemSelections(initialSelections);
    setActivePackageItemIndex(0);
  };

  const updatePackageItemPhoto = (itemIndex, photo) => {
    setPackageItemSelections(prev => ({
      ...prev,
      [itemIndex]: { ...prev[itemIndex], photo_id: photo.id, photo }
    }));
  };

  const updatePackageItemCustomization = (itemIndex, field, value) => {
    setPackageItemSelections(prev => ({
      ...prev,
      [itemIndex]: {
        ...prev[itemIndex],
        customizations: { ...prev[itemIndex]?.customizations, [field]: value }
      }
    }));
  };

  const updatePackageItemBackground = (itemIndex, background) => {
    setPackageItemSelections(prev => ({
      ...prev,
      [itemIndex]: { ...prev[itemIndex], background_id: background?.id || null, background }
    }));
  };

  const handleVariationSelect = (variationName, optionName, priceAdjustment) => {
    setSelectedVariations(prev => ({
      ...prev,
      [variationName]: { name: optionName, price_adjustment: priceAdjustment }
    }));
  };

  const getVariationPriceAdjustment = () => {
    return Object.values(selectedVariations).reduce((sum, v) => sum + (v.price_adjustment || 0), 0);
  };

  const handleAddToCart = () => {
    if (!selectedProduct && !selectedPackage) {
      toast.error('Please select a product or package');
      return;
    }

    // Validate required variations
    if (selectedProduct?.variations) {
      const missingVariations = selectedProduct.variations.filter(v => v.required && !selectedVariations[v.name]);
      if (missingVariations.length > 0) {
        toast.error(`Please select: ${missingVariations.map(v => v.name).join(', ')}`);
        return;
      }
    }

    if (selectedPackage) {
      // Package order
      const itemsArray = Object.values(packageItemSelections);
      const missingPhotos = itemsArray.filter(item => !item.photo_id);
      if (missingPhotos.length > 0) {
        toast.error('Please select a photo for each item in the package');
        return;
      }

      addItem({
        photo_id: selectedPhoto?.id,
        photo: selectedPhoto,
        product_id: null,
        product: null,
        package_id: selectedPackage.id,
        package: selectedPackage,
        package_items: itemsArray.map(sel => ({
          product_id: sel.product_id,
          product: sel.product,
          quantity: sel.quantity,
          photo_id: sel.photo_id,
          photo: sel.photo,
          background_id: sel.background_id,
          background: sel.background,
          customizations: Object.keys(sel.customizations || {}).length > 0 ? sel.customizations : null
        })),
        background_id: null,
        background: null,
        customizations: null,
        selected_variations: null,
        quantity: 1
      });
      toast.success('Package added to cart');
    } else {
      // Individual product order
      const adjustedPrice = (selectedProduct?.price || 0) + getVariationPriceAdjustment();
      addItem({
        photo_id: selectedPhoto.id,
        photo: selectedPhoto,
        product_id: selectedProduct.id,
        product: { ...selectedProduct, price: adjustedPrice },
        package_id: null,
        package: null,
        background_id: selectedBackground?.id || null,
        background: selectedBackground,
        customizations: Object.keys(customizations).length > 0 ? customizations : null,
        selected_variations: Object.keys(selectedVariations).length > 0 ? selectedVariations : null,
        quantity: 1
      });
      toast.success('Added to cart');
    }

    setOrderDialog(false);
  };

  const handleCheckout = async () => {
    if (!customerForm.first_name || !customerForm.last_name) {
      toast.error('Please enter your first and last name');
      return;
    }
    if (!customerForm.email) {
      toast.error('Please enter your email address');
      return;
    }
    if (items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    
    // Skip delivery validation for digital-only orders
    const digitalOnly = isDigitalOnlyOrder();
    
    if (!digitalOnly) {
      // Validate delivery options for physical products
      if (!delivery.option) {
        toast.error('Please select a delivery option');
        return;
      }
      if (!delivery.address || !delivery.city || !delivery.postal_code) {
        toast.error('Please fill in your complete delivery address');
        return;
      }
      if (!delivery.address_type) {
        toast.error('Please indicate if the address is residential or commercial');
        return;
      }
      // Validate no PO Box for mail delivery
      if (delivery.option === 'mail' && delivery.address.toLowerCase().includes('po box')) {
        toast.error('PO Boxes are not accepted for Direct from Lab delivery. Please provide a physical mailing address.');
        return;
      }
    }

    setCheckoutLoading(true);
    try {
      const selectedDeliveryOption = deliveryOptions.find(o => o.id === delivery.option);
      
      // For digital-only orders, send null/empty delivery info
      const deliveryInfo = digitalOnly ? null : {
        option: delivery.option,
        option_name: selectedDeliveryOption?.name,
        fee: selectedDeliveryOption?.fee || 0,
        address: delivery.address,
        city: delivery.city,
        postal_code: delivery.postal_code,
        address_type: delivery.address_type,
        notes: delivery.notes
      };
      
      // Combine first and last names
      const fullName = `${customerForm.first_name} ${customerForm.last_name}`.trim();
      
      const orderData = {
        gallery_id: gallery.id,
        items: items.map(item => ({
          photo_id: item.photo_id,
          product_id: item.product_id,
          package_id: item.package_id,
          package_items: item.package_items || null,
          background_id: item.background_id,
          customizations: item.customizations,
          quantity: item.quantity
        })),
        customer_name: fullName,
        customer_first_name: customerForm.first_name,
        customer_last_name: customerForm.last_name,
        customer_email: customerForm.email,
        customer_phone: customerForm.phone || null,
        discount_code: appliedDiscount?.code || null,
        payment_method: paymentMethod,
        delivery: deliveryInfo
      };

      const orderRes = await axios.post(`${API}/orders`, orderData);
      
      if (paymentMethod === 'cash') {
        // Redirect to cash payment confirmation page
        navigate(`/order/cash-pending/${orderRes.data.id}?name=${encodeURIComponent(fullName)}&total=${getGrandTotal().toFixed(2)}`);
      } else {
        // Proceed with Stripe checkout
        const checkoutRes = await axios.post(`${API}/payments/checkout`, {
          order_id: orderRes.data.id,
          origin_url: window.location.origin
        });
        window.location.href = checkoutRes.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process checkout');
      setCheckoutLoading(false);
    }
  };

  // Get current preview data
  // Get template from selected variation option (if shape variation has a template)
  const getVariationTemplate = () => {
    if (!selectedProduct?.variations || Object.keys(selectedVariations).length === 0) return null;
    
    for (const variation of selectedProduct.variations) {
      const selectedOption = selectedVariations[variation.name];
      if (selectedOption) {
        const option = variation.options?.find(o => o.name === selectedOption.name);
        if (option?.template) {
          return option.template;
        }
      }
    }
    return null;
  };

  const getPreviewData = () => {
    if (selectedPackage && Object.keys(packageItemSelections).length > 0) {
      const activeItem = packageItemSelections[activePackageItemIndex];
      return {
        photo: activeItem?.photo,
        background: activeItem?.background,
        template: activeItem?.product?.template
      };
    }
    
    // Check for variation-specific template first, fall back to product template
    const variationTemplate = getVariationTemplate();
    
    return {
      photo: selectedPhoto,
      background: selectedBackground,
      template: variationTemplate || selectedProduct?.template
    };
  };

  const previewData = getPreviewData();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Camera className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold text-slate-900 mb-2">Gallery Not Found</h1>
          <p className="text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <img 
              src="https://customer-assets.emergentagent.com/job_photo-gallery-orders/artifacts/59pp4syw_BEP%20Logo%202017-1.png" 
              alt="Bright Eyes Photography"
              className="h-10 w-auto"
            />
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="relative border-stone-300" data-testid="cart-btn">
                  <ShoppingCart className="h-5 w-5" />
                  {itemCount > 0 && (
                    <Badge className="absolute -top-2 -right-2 bg-amber-600 text-white h-5 w-5 p-0 flex items-center justify-center text-xs">
                      {itemCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-lg flex flex-col overflow-y-auto" data-testid="cart-sheet">
                <SheetHeader>
                  <SheetTitle className="font-heading">Your Cart</SheetTitle>
                  <SheetDescription>
                    {itemCount === 0 ? 'Your cart is empty' : `${itemCount} item(s) in cart`}
                  </SheetDescription>
                </SheetHeader>
                
                {items.length > 0 ? (
                  <>
                    <div className="my-4">
                      <div className="space-y-3 pr-4">
                        {items.map((item) => (
                          <div key={item.id} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                            <img
                              src={item.photo?.thumbnail_url ? `${process.env.REACT_APP_BACKEND_URL}${item.photo.thumbnail_url}` : getWatermarkedUrl(item.photo)}
                              alt=""
                              className="w-14 h-14 object-cover rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">
                                {item.package?.name || item.product?.name}
                              </p>
                              {item.background && (
                                <p className="text-xs text-slate-500">BG: {item.background.name}</p>
                              )}
                              {item.customizations && (
                                <p className="text-xs text-slate-500 truncate">
                                  {Object.entries(item.customizations).map(([k, v]) => `${k}: ${v}`).join(', ')}
                                </p>
                              )}
                              <div className="flex items-center justify-between mt-1">
                                <div className="flex items-center gap-1">
                                  <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => updateQuantity(item.id, item.quantity - 1)}>
                                    <Minus className="h-3 w-3" />
                                  </Button>
                                  <span className="w-6 text-center text-sm">{item.quantity}</span>
                                  <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => updateQuantity(item.id, item.quantity + 1)}>
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => removeItem(item.id)}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            <span className="font-bold text-sm">
                              {formatCurrency((item.package?.price || item.product?.price || 0) * item.quantity)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Customer Info */}
                    <div className="space-y-3 py-4 border-t">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label>First Name *</Label>
                          <Input
                            placeholder="First name"
                            value={customerForm.first_name}
                            onChange={(e) => setCustomerForm({ ...customerForm, first_name: e.target.value })}
                            data-testid="customer-first-name-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>Last Name *</Label>
                          <Input
                            placeholder="Last name"
                            value={customerForm.last_name}
                            onChange={(e) => setCustomerForm({ ...customerForm, last_name: e.target.value })}
                            data-testid="customer-last-name-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          placeholder="your@email.com"
                          value={customerForm.email}
                          onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                          data-testid="customer-email-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>Phone *</Label>
                        <Input
                          placeholder="Phone number"
                          value={customerForm.phone}
                          onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                          data-testid="customer-phone-input"
                        />
                      </div>
                    </div>

                    {/* Delivery Options - Hidden for digital-only orders */}
                    {!isDigitalOnlyOrder() && (
                      <div className="border-t pt-3">
                        <Label className="flex items-center gap-2 mb-3">
                          <Truck className="h-4 w-4 text-amber-600" />
                          Delivery Option *
                        </Label>
                        <div className="space-y-2">
                          {deliveryOptions.map((option) => (
                            <div 
                              key={option.id}
                              className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                delivery.option === option.id 
                                  ? 'border-amber-500 bg-amber-50' 
                                  : 'border-stone-200 hover:border-stone-300'
                              }`}
                              onClick={() => setDelivery({ ...delivery, option: option.id })}
                              data-testid={`delivery-option-${option.id}`}
                            >
                              <div className="flex items-center gap-2">
                                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                  delivery.option === option.id ? 'border-amber-500' : 'border-stone-300'
                                }`}>
                                  {delivery.option === option.id && <div className="w-2 h-2 rounded-full bg-amber-500" />}
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-medium">{option.name}</p>
                                  <p className="text-xs text-stone-500">{option.description}</p>
                                </div>
                                <span className="text-sm font-semibold text-amber-600">${option.fee.toFixed(2)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {delivery.option && (
                          <div className="mt-3 space-y-2 p-3 bg-stone-50 rounded-lg">
                            <div className="space-y-1">
                              <Label className="text-xs">Street Address *</Label>
                              <Input
                                placeholder={delivery.option === 'mail' ? 'Mailing address (no PO Boxes)' : 'Street address'}
                                value={delivery.address}
                                onChange={(e) => setDelivery({ ...delivery, address: e.target.value })}
                                className="h-8 text-sm"
                                data-testid="delivery-address"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <Label className="text-xs">City *</Label>
                                <Input
                                  placeholder="City"
                                  value={delivery.city}
                                  onChange={(e) => setDelivery({ ...delivery, city: e.target.value })}
                                  className="h-8 text-sm"
                                  data-testid="delivery-city"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Postal Code *</Label>
                                <Input
                                  placeholder="T7X 1A1"
                                  value={delivery.postal_code}
                                  onChange={(e) => setDelivery({ ...delivery, postal_code: e.target.value })}
                                  className="h-8 text-sm"
                                  data-testid="delivery-postal"
                                />
                              </div>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Address Type *</Label>
                              <div className="flex gap-2">
                                <div 
                                  className={`flex-1 p-2 rounded border-2 cursor-pointer text-center text-xs ${
                                    delivery.address_type === 'residential' 
                                      ? 'border-amber-500 bg-amber-50' 
                                      : 'border-stone-200 hover:border-stone-300'
                                  }`}
                                  onClick={() => setDelivery({ ...delivery, address_type: 'residential' })}
                                  data-testid="address-type-residential"
                                >
                                  🏠 Residential
                                </div>
                                <div 
                                  className={`flex-1 p-2 rounded border-2 cursor-pointer text-center text-xs ${
                                    delivery.address_type === 'commercial' 
                                      ? 'border-amber-500 bg-amber-50' 
                                      : 'border-stone-200 hover:border-stone-300'
                                  }`}
                                  onClick={() => setDelivery({ ...delivery, address_type: 'commercial' })}
                                  data-testid="address-type-commercial"
                                >
                                  🏢 Commercial
                                </div>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Delivery Notes (Optional)</Label>
                              <Input
                                placeholder="Special instructions, buzzer code, etc."
                                value={delivery.notes}
                                onChange={(e) => setDelivery({ ...delivery, notes: e.target.value })}
                                className="h-8 text-sm"
                                data-testid="delivery-notes"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Discount */}
                    <div className="border-t pt-3">
                      {appliedDiscount ? (
                        <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Tag className="h-4 w-4 text-green-600" />
                            <span className="text-sm font-medium text-green-700">{appliedDiscount.code}</span>
                          </div>
                          <Button variant="ghost" size="sm" onClick={removeDiscount}><X className="h-4 w-4" /></Button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Input
                            placeholder="Discount code"
                            value={discountCode}
                            onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                            className="font-mono uppercase text-sm"
                          />
                          <Button variant="outline" size="sm" onClick={applyDiscountCode}>Apply</Button>
                        </div>
                      )}
                    </div>

                    {/* Payment Method */}
                    <div className="border-t pt-3">
                      <Label className="flex items-center gap-2 mb-3">
                        <CreditCard className="h-4 w-4 text-amber-600" />
                        Payment Method
                      </Label>
                      <div className="space-y-2">
                        <div 
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            paymentMethod === 'cash' 
                              ? 'border-green-500 bg-green-50' 
                              : 'border-stone-200 hover:border-stone-300'
                          }`}
                          onClick={() => setPaymentMethod('cash')}
                          data-testid="payment-cash"
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              paymentMethod === 'cash' ? 'border-green-500' : 'border-stone-300'
                            }`}>
                              {paymentMethod === 'cash' && <div className="w-2 h-2 rounded-full bg-green-500" />}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-green-700 flex items-center gap-2">
                                Pay with CASH
                              </p>
                              <p className="text-xs text-stone-500">Pay at pickup - exact change appreciated</p>
                            </div>
                          </div>
                        </div>

                        <div 
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            paymentMethod === 'card' 
                              ? 'border-amber-500 bg-amber-50' 
                              : 'border-stone-200 hover:border-stone-300'
                          }`}
                          onClick={() => setPaymentMethod('card')}
                          data-testid="payment-card"
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              paymentMethod === 'card' ? 'border-amber-500' : 'border-stone-300'
                            }`}>
                              {paymentMethod === 'card' && <div className="w-2 h-2 rounded-full bg-amber-500" />}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium">Pay with Card</p>
                              <p className="text-xs text-stone-500">Visa, Mastercard, Amex</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Totals */}
                    <div className="border-t pt-3 space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>Subtotal</span>
                        <span>{formatCurrency(getTotal())}</span>
                      </div>
                      {appliedDiscount && (
                        <div className="flex justify-between text-sm text-green-600">
                          <span>Discount</span>
                          <span>-{formatCurrency(appliedDiscount.discount_amount)}</span>
                        </div>
                      )}
                      {galleryTaxEnabled && (
                        <div className="flex justify-between text-sm">
                          <span>Tax ({galleryTaxRate}%)</span>
                          <span>{formatCurrency(getTaxAmount())}</span>
                        </div>
                      )}
                      {getDeliveryFee() > 0 && (
                        <div className="flex justify-between text-sm">
                          <span>{deliveryOptions.find(o => o.id === delivery.option)?.name || 'Delivery'}</span>
                          <span>{formatCurrency(getDeliveryFee())}</span>
                        </div>
                      )}
                      {getCashDiscount() > 0 && (
                        <div className="flex justify-between text-sm text-green-600 font-medium">
                          <span>Cash Discount (10%)</span>
                          <span>-{formatCurrency(getCashDiscount())}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-lg pt-1">
                        <span>Total</span>
                        <span className={paymentMethod === 'cash' ? 'text-green-600' : 'text-amber-600'}>{formatCurrency(getGrandTotal())}</span>
                      </div>
                    </div>

                    <SheetFooter className="mt-4">
                      <Button
                        className={`w-full ${paymentMethod === 'cash' ? 'bg-green-600 hover:bg-green-700' : 'btn-action'}`}
                        size="lg"
                        onClick={handleCheckout}
                        disabled={checkoutLoading || isExpired}
                        data-testid="checkout-btn"
                      >
                        {isExpired ? 'Gallery Expired' : checkoutLoading ? 'Processing...' : paymentMethod === 'cash' ? `Reserve Order ${formatCurrency(getGrandTotal())}` : `Checkout ${formatCurrency(getGrandTotal())}`}
                      </Button>
                    </SheetFooter>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <ShoppingCart className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">Your cart is empty</p>
                    </div>
                  </div>
                )}
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Gallery Info */}
      <div className="bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
          <h1 className="text-3xl sm:text-4xl font-heading text-stone-800">{gallery.name}</h1>
          {gallery.description && <p className="text-stone-600 mt-2">{gallery.description}</p>}
          <div className="flex items-center gap-4 mt-4">
            <Badge className="bg-amber-100 text-amber-700 capitalize">{gallery.event_type}</Badge>
            <span className="text-stone-500">{photos.length} photos</span>
          </div>
          {isExpired && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Gallery Ordering Closed</p>
                <p className="text-sm text-amber-600">This gallery is no longer accepting new orders.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Photo Grid */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        {photos.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {photos.map((photo) => (
              <div 
                key={photo.id} 
                className="group cursor-pointer"
                onClick={() => openPhotoOrder(photo)}
                data-testid={`photo-${photo.id}`}
              >
                <div className="relative aspect-square overflow-hidden rounded-lg border border-stone-200 bg-stone-100">
                  <img
                    src={getWatermarkedUrl(photo)}
                    alt={photo.filename}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <Button size="sm" className="btn-action opacity-0 group-hover:opacity-100 transition-opacity">
                      <Plus className="h-4 w-4 mr-1" /> Order
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Image className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-heading font-semibold text-slate-900">No photos yet</h2>
            <p className="text-slate-500 mt-2">Photos will appear here once uploaded</p>
          </div>
        )}
      </div>

      {/* Order Dialog - Simple Two Column */}
      <Dialog open={orderDialog} onOpenChange={setOrderDialog}>
        <DialogContent className="max-w-[900px] w-[95vw] max-h-[90vh] p-0" data-testid="order-dialog">
          <div className="flex h-full max-h-[85vh]">
            {/* Left Side - Options */}
            <div className="flex-1 min-w-0 overflow-y-auto p-6 pr-4">
              <DialogHeader className="mb-4">
                <DialogTitle className="font-heading text-xl">Customize Your Order</DialogTitle>
                <DialogDescription>Select a product or package</DialogDescription>
              </DialogHeader>

              {selectedPhoto && (
                <div className="space-y-6">
                  {/* Packages */}
                  {packages.length > 0 && (
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2 text-base font-semibold">
                        <Package className="h-5 w-5 text-amber-600" /> Packages
                      </Label>
                      <div className="grid grid-cols-2 gap-2">
                        {packages.map((pkg) => (
                          <Card
                            key={pkg.id}
                            className={`cursor-pointer transition-all ${selectedPackage?.id === pkg.id ? 'border-amber-500 ring-2 ring-amber-500 bg-amber-50' : 'hover:border-amber-300'}`}
                            onClick={() => handlePackageSelect(pkg)}
                          >
                            <CardContent className="p-3">
                              <div className="flex justify-between items-start">
                                <p className="font-medium text-sm">{pkg.name}</p>
                                <span className="font-bold text-amber-700">{formatCurrency(pkg.price)}</span>
                              </div>
                              <p className="text-xs text-slate-500 mt-1">{pkg.items?.length || 0} items</p>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Package Item Configuration */}
                  {selectedPackage && Object.keys(packageItemSelections).length > 0 && (
                    <div className="space-y-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <Label className="text-amber-800 font-medium">Configure Each Item</Label>
                      
                      {/* Item Tabs */}
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(packageItemSelections).map(([index, sel]) => (
                          <Button
                            key={index}
                            variant={parseInt(index) === activePackageItemIndex ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setActivePackageItemIndex(parseInt(index))}
                            className={parseInt(index) === activePackageItemIndex ? 'bg-amber-600' : ''}
                          >
                            {sel.product?.name || `Item ${parseInt(index) + 1}`}
                            {sel.photo_id && <Check className="h-3 w-3 ml-1" />}
                          </Button>
                        ))}
                      </div>

                      {/* Active Item Config */}
                      {packageItemSelections[activePackageItemIndex] && (
                        <div className="space-y-3 p-3 bg-white rounded border">
                          <p className="font-medium">{packageItemSelections[activePackageItemIndex].product?.name}</p>
                          
                          {/* Photo Selection */}
                          <div>
                            <Label className="text-xs">Select Photo</Label>
                            <div className="grid grid-cols-6 gap-1 mt-1 max-h-24 overflow-y-auto">
                              {photos.map((photo) => (
                                <div
                                  key={photo.id}
                                  className={`aspect-square rounded cursor-pointer overflow-hidden ${packageItemSelections[activePackageItemIndex]?.photo_id === photo.id ? 'ring-2 ring-amber-500' : 'border'}`}
                                  onClick={() => updatePackageItemPhoto(activePackageItemIndex, photo)}
                                >
                                  <img src={photo.thumbnail_url ? `${process.env.REACT_APP_BACKEND_URL}${photo.thumbnail_url}` : getWatermarkedUrl(photo)} alt="" className="w-full h-full object-cover" />
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Background */}
                          {packageItemSelections[activePackageItemIndex]?.product?.allows_background && backgrounds.length > 0 && (
                            <div>
                              <Label className="text-xs">Background</Label>
                              <div className="flex gap-1 mt-1 overflow-x-auto">
                                <div
                                  className={`w-12 h-12 rounded flex-shrink-0 flex items-center justify-center text-xs border cursor-pointer ${!packageItemSelections[activePackageItemIndex]?.background_id ? 'border-amber-500 bg-amber-50' : ''}`}
                                  onClick={() => updatePackageItemBackground(activePackageItemIndex, null)}
                                >None</div>
                                {backgrounds.map((bg) => (
                                  <div
                                    key={bg.id}
                                    className={`w-12 h-12 rounded flex-shrink-0 overflow-hidden cursor-pointer ${packageItemSelections[activePackageItemIndex]?.background_id === bg.id ? 'ring-2 ring-amber-500' : 'border'}`}
                                    onClick={() => updatePackageItemBackground(activePackageItemIndex, bg)}
                                  >
                                    <img src={bg.thumbnail_url || bg.url} alt={bg.name} className="w-full h-full object-cover" />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Customizations */}
                          {packageItemSelections[activePackageItemIndex]?.product?.customization_fields?.length > 0 && (
                            <div className="space-y-2">
                              <Label className="text-xs text-slate-600">Optional Customizations (no extra fee)</Label>
                              <div className="grid grid-cols-2 gap-2">
                                {packageItemSelections[activePackageItemIndex].product.customization_fields.map((field) => (
                                  <Input
                                    key={field}
                                    placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
                                    value={packageItemSelections[activePackageItemIndex]?.customizations?.[field] || ''}
                                    onChange={(e) => updatePackageItemCustomization(activePackageItemIndex, field, e.target.value)}
                                    className="text-sm"
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Individual Products */}
                  {!selectedPackage && (
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2 text-base font-semibold">
                        <Image className="h-5 w-5 text-blue-600" />
                        {packages.length > 0 ? 'Or Select Product' : 'Select Product'}
                      </Label>
                      <Select
                        value={selectedProduct?.id || ''}
                        onValueChange={(id) => handleProductSelect(products.find(p => p.id === id))}
                      >
                        <SelectTrigger data-testid="product-select">
                          <SelectValue placeholder="Choose a product" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map((product) => (
                            <SelectItem key={product.id} value={product.id}>
                              {product.name} - {formatCurrency(product.price)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Variations */}
                  {selectedProduct?.variations?.length > 0 && (
                    <div className="space-y-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <Label className="text-purple-800 font-medium">Options</Label>
                      {selectedProduct.variations.map((variation) => (
                        <div key={variation.name}>
                          <Label className="text-sm">{variation.name}{variation.required && <span className="text-red-500">*</span>}</Label>
                          <Select
                            value={selectedVariations[variation.name]?.name || ''}
                            onValueChange={(optionName) => {
                              const option = variation.options.find(o => o.name === optionName);
                              handleVariationSelect(variation.name, optionName, option?.price_adjustment || 0);
                            }}
                          >
                            <SelectTrigger className="mt-1"><SelectValue placeholder={`Select ${variation.name}`} /></SelectTrigger>
                            <SelectContent>
                              {variation.options.map((opt) => (
                                <SelectItem key={opt.name} value={opt.name}>
                                  {opt.name}{opt.price_adjustment > 0 && ` (+${formatCurrency(opt.price_adjustment)})`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Background for Products */}
                  {selectedProduct?.allows_background && backgrounds.length > 0 && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><Palette className="h-4 w-4" /> Background</Label>
                      <div className="flex gap-2 flex-wrap">
                        <div
                          className={`w-14 h-14 rounded flex items-center justify-center text-xs border cursor-pointer ${!selectedBackground ? 'border-blue-500 bg-blue-50' : ''}`}
                          onClick={() => setSelectedBackground(null)}
                        >None</div>
                        {backgrounds.map((bg) => (
                          <div
                            key={bg.id}
                            className={`w-14 h-14 rounded overflow-hidden cursor-pointer ${selectedBackground?.id === bg.id ? 'ring-2 ring-blue-500' : 'border'}`}
                            onClick={() => setSelectedBackground(bg)}
                          >
                            <img src={bg.thumbnail_url || bg.url} alt={bg.name} className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Customization Fields */}
                  {selectedProduct?.customization_fields?.length > 0 && (
                    <div className="space-y-2">
                      <Label>Optional Customizations <span className="font-normal text-slate-500">(no extra fee)</span></Label>
                      <div className="grid grid-cols-2 gap-2">
                        {selectedProduct.customization_fields.map((field) => (
                          <div key={field}>
                            <Label className="text-xs capitalize">{field}</Label>
                            <Input
                              placeholder={`Enter ${field}`}
                              value={customizations[field] || ''}
                              onChange={(e) => setCustomizations({ ...customizations, [field]: e.target.value })}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add to Cart */}
                  <div className="flex gap-3 pt-4 border-t">
                    <Button variant="outline" onClick={() => setOrderDialog(false)} className="flex-1">Cancel</Button>
                    <Button 
                      className="btn-action flex-1" 
                      onClick={handleAddToCart}
                      disabled={!selectedProduct && !selectedPackage}
                      data-testid="add-to-cart-btn"
                    >
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Add to Cart {(selectedProduct || selectedPackage) && formatCurrency(
                        selectedPackage?.price || (selectedProduct?.price || 0) + getVariationPriceAdjustment()
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Right Side - Simple Preview */}
            <div className="w-64 flex-shrink-0 bg-slate-100 border-l flex flex-col">
              <div className="p-3 bg-slate-200 border-b">
                <p className="font-medium text-slate-700 text-sm">Preview</p>
              </div>
              <div className="flex-1 p-3 flex flex-col items-center justify-start overflow-y-auto">
                {previewData.photo ? (
                  <div className="space-y-2 w-full">
                    {/* Photo Preview */}
                    <div className="relative aspect-square bg-white rounded-lg overflow-hidden border shadow-sm">
                      {/* Background layer */}
                      {previewData.background && (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${previewData.background.url}`}
                          alt="Background"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      )}
                      {/* Photo layer */}
                      <img
                        src={getWatermarkedUrl(previewData.photo)}
                        alt="Selected photo"
                        className={`relative w-full h-full ${previewData.background ? 'object-contain p-4' : 'object-cover'}`}
                      />
                      {/* Template overlay */}
                      {previewData.template && (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${previewData.template.url}`}
                          alt="Template"
                          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                        />
                      )}
                    </div>

                    {/* Selected Options Summary */}
                    <div className="text-xs space-y-0.5">
                      {(selectedProduct || selectedPackage) && (
                        <p className="font-medium text-sm">{selectedProduct?.name || selectedPackage?.name}</p>
                      )}
                      {selectedBackground && (
                        <p className="text-slate-600">BG: {selectedBackground.name}</p>
                      )}
                      {Object.entries(selectedVariations).length > 0 && (
                        <div className="text-slate-600">
                          {Object.entries(selectedVariations).map(([k, v]) => (
                            <p key={k}>{k}: {v.name}</p>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Price */}
                    {(selectedProduct || selectedPackage) && (
                      <div className="text-center pt-2 border-t">
                        <span className="text-xl font-bold text-amber-600">
                          {formatCurrency((selectedProduct?.price || selectedPackage?.price || 0) + getVariationPriceAdjustment())}
                        </span>
                      </div>
                    )}

                    {/* Preview Disclaimer */}
                    <p className="text-[10px] text-slate-400 text-center italic leading-tight pt-1 border-t">
                      Preview is for reference only. Final editing will be done professionally to ensure proper proportions and placement.
                    </p>
                  </div>
                ) : (
                  <div className="text-center text-slate-400">
                    <Image className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Select options to see preview</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ClientGalleryPage = () => (
  <CartProvider>
    <ClientGalleryContent />
  </CartProvider>
);

export default ClientGalleryPage;
